import { useEffect, useState } from 'react';
import React, { useMemo } from 'react';
import {
  ISOEnterKeycapShadow,
  OnePointFiveUnitKeycapShadow,
  OnePointSevenFiveUnitKeycapShadow,
  OnePointTwoFiveUnitKeycapShadow,
  OneUnitKeycapShadow,
  OneUnitHomingBarKeycapShadow,
  OnePointSevenFiveUnitSteppedKeycapShadow,
  ConvexOneUnitKeycapShadow,
  ConvexOnePointTwoFiveUnitKeycapShadow,
  ConvexOnePointSevenFiveUnitKeycapShadow,
  ConvexTwoUnitKeycapShadow,
  ConvexTwoPointTwoFiveUnitKeycapShadow,
  ConvexTwoPointSevenFiveUnitKeycapShadow,
  ConvexThreeUnitKeycapShadow,
  ConvexSixPointTwoFiveUnitKeycapShadow,
  ConvexSevenUnitKeycapShadow,
  TwoPointSevenFiveUnitKeycapShadow,
  TwoPointTwoFiveUnitKeycapShadow,
  TwoUnitKeycapShadow,
} from '../KeycapShadows';
import { getSlotShadowPosition } from '../../../../utils/moldGeometry';

const imageUrls = {
  oneUnitKeycapShadow: '/images/keycapShadows/1u-shadow.png',
  oneUnitHomingBarKeycapShadow:
    '/images/keycapShadows/1u-homing-bar-shadow.png',
  onePointFiveUnitKeycapShadow: '/images/keycapShadows/1.5u-shadow.png',
  onePointTwoFiveUnitKeycapShadow: '/images/keycapShadows/1.25u-shadow.png',
  onePointSevenFiveUnitKeycapShadow: '/images/keycapShadows/1.75u-shadow.png',
  oneTimesTwoUnitKeycapShadow: '/images/keycapShadows/2u-vertical-shadow.png',
  twoUnitKeycapShadow: '/images/keycapShadows/2u-shadow.png',
  twoPointTwoFiveUnitKeycapShadow: '/images/keycapShadows/2.25u-shadow.png',
  twoPointSevenFiveUnitKeycapShadow: '/images/keycapShadows/2.75u-shadow.png',
  isoEnterKeycapShadow: '/images/keycapShadows/iso-enter-shadow.png',
  onePointSevenFiveUnitSteppedKeycapShadow:
    '/images/keycapShadows/1.75u-stepped-shadow.png',
  convexOneUnitKeycapShadow: '/images/keycapShadows/convex-1u-shadow.png',
  convexOnePointTwoFiveUnitKeycapShadow:
    '/images/keycapShadows/convex-1.25u-shadow.png',
  convexOnePointSevenFiveUnitKeycapShadow:
    '/images/keycapShadows/convex-1.75u-shadow.png',
  convexTwoUnitKeycapShadow: '/images/keycapShadows/convex-2u-shadow.png',
  convexTwoPointTwoFiveUnitKeycapShadow:
    '/images/keycapShadows/convex-2.25u-shadow.png',
  convexTwoPointSevenFiveUnitKeycapShadow:
    '/images/keycapShadows/convex-2.75u-shadow.png',
  convexThreeUnitKeycapShadow: '/images/keycapShadows/convex-3u-shadow.png',
  convexSixPointTwoFiveUnitKeycapShadow:
    '/images/keycapShadows/convex-6.25u-shadow.png',
  convexSevenUnitKeycapShadow: '/images/keycapShadows/convex-7u-shadow.png',
};

const fetchAllImagesAsDataUrls = async (imageUrls) => {
  const promises = Object.entries(imageUrls).map(([key, url]) =>
    fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve([key, reader.result]);
          reader.readAsDataURL(blob);
        });
      })
  );

  const results = await Promise.all(promises);
  return Object.fromEntries(results);
};

const StaticKeycapShadows = ({ keycapData, triggerKeycapShadowRerender }) => {
  const [imageDataUrls, setImageDataUrls] = useState({});

  useEffect(() => {
    fetchAllImagesAsDataUrls(imageUrls).then(setImageDataUrls);
  }, []);

  const staticKeycapShadows = useMemo(() => {
    // using useMemo to create a memoized version of the static shadow components. This ensures that these components are only re-computed when specific dependencies (inside the dependency array) change. This way the keycap shadows do not re-compute everytime, the user makes a change to the design.
    let components = []; // an array that will hold all the static components (keycap shadows)

    keycapData.forEach((keycap, keycapIndex) => {
      const { x: xPosForKeycap, y: yPosForRow } = getSlotShadowPosition(keycap); // the shadow's top-left corner at the keycap's slot

      if (keycap.isIsoEnter) {
        // a shadow for a ISO Enter key will be created and added to the components array
        const shadowComponent = (
          <ISOEnterKeycapShadow
            key={`shadow-${keycapIndex}`}
            keycapIndex={keycapIndex}
            xPosForKeycap={xPosForKeycap}
            yPosForRow={yPosForRow}
            keycap={keycap}
            imageDataUrl={imageDataUrls.isoEnterKeycapShadow}
          />
        );
        components.push(shadowComponent);
      } else if (keycap.isConvex) {
        if (keycap.width === 1 && keycap.height === 1) {
          // a shadow for a 1 unit key (spacebar) will be created and added to the components array
          const shadowComponent = (
            <ConvexOneUnitKeycapShadow
              key={`shadow-${keycapIndex}`}
              keycapIndex={keycapIndex}
              xPosForKeycap={xPosForKeycap}
              yPosForRow={yPosForRow}
              keycap={keycap}
              imageDataUrl={imageDataUrls.convexOneUnitKeycapShadow}
            />
          );
          components.push(shadowComponent);
        } else if (keycap.width === 1.25 && keycap.height === 1) {
          // a shadow for a 1.25 unit key (spacebar) will be created and added to the components array
          const shadowComponent = (
            <ConvexOnePointTwoFiveUnitKeycapShadow
              key={`shadow-${keycapIndex}`}
              keycapIndex={keycapIndex}
              xPosForKeycap={xPosForKeycap}
              yPosForRow={yPosForRow}
              keycap={keycap}
              imageDataUrl={imageDataUrls.convexOnePointTwoFiveUnitKeycapShadow}
            />
          );
          components.push(shadowComponent);
        } else if (keycap.width === 1.75 && keycap.height === 1) {
          // a shadow for a 1.75 unit key (spacebar) will be created and added to the components array
          const shadowComponent = (
            <ConvexOnePointSevenFiveUnitKeycapShadow
              key={`shadow-${keycapIndex}`}
              keycapIndex={keycapIndex}
              xPosForKeycap={xPosForKeycap}
              yPosForRow={yPosForRow}
              keycap={keycap}
              imageDataUrl={
                imageDataUrls.convexOnePointSevenFiveUnitKeycapShadow
              }
            />
          );
          components.push(shadowComponent);
        } else if (keycap.width === 2 && keycap.height === 1) {
          // a shadow for a 2 unit key (spacebar) will be created and added to the components array
          const shadowComponent = (
            <ConvexTwoUnitKeycapShadow
              key={`shadow-${keycapIndex}`}
              keycapIndex={keycapIndex}
              xPosForKeycap={xPosForKeycap}
              yPosForRow={yPosForRow}
              keycap={keycap}
              imageDataUrl={imageDataUrls.convexTwoUnitKeycapShadow}
            />
          );
          components.push(shadowComponent);
        } else if (keycap.width === 2.25 && keycap.height === 1) {
          // a shadow for a 2.25 unit key (spacebar) will be created and added to the components array
          const shadowComponent = (
            <ConvexTwoPointTwoFiveUnitKeycapShadow
              key={`shadow-${keycapIndex}`}
              keycapIndex={keycapIndex}
              xPosForKeycap={xPosForKeycap}
              yPosForRow={yPosForRow}
              keycap={keycap}
              imageDataUrl={imageDataUrls.convexTwoPointTwoFiveUnitKeycapShadow}
            />
          );
          components.push(shadowComponent);
        } else if (keycap.width === 2.75 && keycap.height === 1) {
          // a shadow for a 2.75 unit key (spacebar) will be created and added to the components array
          const shadowComponent = (
            <ConvexTwoPointSevenFiveUnitKeycapShadow
              key={`shadow-${keycapIndex}`}
              keycapIndex={keycapIndex}
              xPosForKeycap={xPosForKeycap}
              yPosForRow={yPosForRow}
              keycap={keycap}
              imageDataUrl={
                imageDataUrls.convexTwoPointSevenFiveUnitKeycapShadow
              }
            />
          );
          components.push(shadowComponent);
        } else if (keycap.width === 3 && keycap.height === 1) {
          // a shadow for a 3 unit key (spacebar) will be created and added to the components array
          const shadowComponent = (
            <ConvexThreeUnitKeycapShadow
              key={`shadow-${keycapIndex}`}
              keycapIndex={keycapIndex}
              xPosForKeycap={xPosForKeycap}
              yPosForRow={yPosForRow}
              keycap={keycap}
              imageDataUrl={imageDataUrls.convexThreeUnitKeycapShadow}
            />
          );
          components.push(shadowComponent);
        } else if (keycap.width === 6.25 && keycap.height === 1) {
          // a shadow for a 6.25 unit key (spacebar) will be created and added to the components array
          const shadowComponent = (
            <ConvexSixPointTwoFiveUnitKeycapShadow
              key={`shadow-${keycapIndex}`}
              keycapIndex={keycapIndex}
              xPosForKeycap={xPosForKeycap}
              yPosForRow={yPosForRow}
              keycap={keycap}
              imageDataUrl={imageDataUrls.convexSixPointTwoFiveUnitKeycapShadow}
            />
          );
          components.push(shadowComponent);
        } else if (keycap.width === 7 && keycap.height === 1) {
          // a shadow for a 7 unit key (spacebar) will be created and added to the components array
          const shadowComponent = (
            <ConvexSevenUnitKeycapShadow
              key={`shadow-${keycapIndex}`}
              keycapIndex={keycapIndex}
              xPosForKeycap={xPosForKeycap}
              yPosForRow={yPosForRow}
              keycap={keycap}
              imageDataUrl={imageDataUrls.convexSevenUnitKeycapShadow}
            />
          );
          components.push(shadowComponent);
        }
      } else if (keycap.stepped) {
        const shadowComponent = (
          <OnePointSevenFiveUnitSteppedKeycapShadow
            key={`shadow-${keycapIndex}`}
            keycapIndex={keycapIndex}
            xPosForKeycap={xPosForKeycap}
            yPosForRow={yPosForRow}
            keycap={keycap}
            imageDataUrl={
              imageDataUrls.onePointSevenFiveUnitSteppedKeycapShadow
            }
          />
        );
        components.push(shadowComponent);
      } else if (keycap.hasHomingBar) {
        const shadowComponent = (
          <OneUnitHomingBarKeycapShadow
            key={`shadow-${keycapIndex}`}
            keycapIndex={keycapIndex}
            xPosForKeycap={xPosForKeycap}
            yPosForRow={yPosForRow}
            keycap={keycap}
            imageDataUrl={imageDataUrls.oneUnitHomingBarKeycapShadow}
          />
        );
        components.push(shadowComponent);
      } else {
        if (keycap.width === 1 && keycap.height === 1) {
          // a shadow for a 1 unit key will be created and added to the components array
          const shadowComponent = (
            <OneUnitKeycapShadow
              key={`shadow-${keycapIndex}`}
              keycapIndex={keycapIndex}
              xPosForKeycap={xPosForKeycap}
              yPosForRow={yPosForRow}
              keycap={keycap}
              imageDataUrl={imageDataUrls.oneUnitKeycapShadow}
            />
          );
          components.push(shadowComponent);
        } else if (keycap.width === 1.5 && keycap.height === 1) {
          // a shadow for a 1.5 unit key will be created and added to the components array
          const shadowComponent = (
            <OnePointFiveUnitKeycapShadow
              key={`shadow-${keycapIndex}`}
              keycapIndex={keycapIndex}
              xPosForKeycap={xPosForKeycap}
              yPosForRow={yPosForRow}
              keycap={keycap}
              imageDataUrl={imageDataUrls.onePointFiveUnitKeycapShadow}
            />
          );
          components.push(shadowComponent);
        } else if (keycap.width === 1.25 && keycap.height === 1) {
          // a shadow for a 1.25 unit key will be created and added to the components array
          const shadowComponent = (
            <OnePointTwoFiveUnitKeycapShadow
              key={`shadow-${keycapIndex}`}
              keycapIndex={keycapIndex}
              xPosForKeycap={xPosForKeycap}
              yPosForRow={yPosForRow}
              keycap={keycap}
              imageDataUrl={imageDataUrls.onePointTwoFiveUnitKeycapShadow}
            />
          );
          components.push(shadowComponent);
        } else if (keycap.width === 1.75 && keycap.height === 1) {
          // a shadow for a 1.75 unit key will be created and added to the components array
          const shadowComponent = (
            <OnePointSevenFiveUnitKeycapShadow
              key={`shadow-${keycapIndex}`}
              keycapIndex={keycapIndex}
              xPosForKeycap={xPosForKeycap}
              yPosForRow={yPosForRow}
              keycap={keycap}
              imageDataUrl={imageDataUrls.onePointSevenFiveUnitKeycapShadow}
            />
          );
          components.push(shadowComponent);
        } else if (keycap.width === 1 && keycap.height === 2) {
          // a shadow for a 1-by-2 unit key will be created and added to the components array: the lying 2u shadow, as every 1x2 goes on a 2x1 slot of the XDA molds, where the customizer's copy draws it standing (OneTimesTwoUnitKeycapShadow); the two components stay apart for that one difference. Known bug (KNOWN_BUGS.md, 1x2 shadows on other profiles)
          const shadowComponent = (
            <TwoUnitKeycapShadow
              key={`shadow-${keycapIndex}`}
              keycapIndex={keycapIndex}
              xPosForKeycap={xPosForKeycap}
              yPosForRow={yPosForRow}
              keycap={keycap}
              imageDataUrl={imageDataUrls.twoUnitKeycapShadow}
            />
          );
          components.push(shadowComponent);
        } else if (keycap.width === 2.25 && keycap.height === 1) {
          // a shadow for a 2.25 unit key will be created and added to the components array
          const shadowComponent = (
            <TwoPointTwoFiveUnitKeycapShadow
              key={`shadow-${keycapIndex}`}
              keycapIndex={keycapIndex}
              xPosForKeycap={xPosForKeycap}
              yPosForRow={yPosForRow}
              keycap={keycap}
              imageDataUrl={imageDataUrls.twoPointTwoFiveUnitKeycapShadow}
            />
          );
          components.push(shadowComponent);
        } else if (keycap.width === 2.75 && keycap.height === 1) {
          // a shadow for a 2.75 unit key will be created and added to the components array
          const shadowComponent = (
            <TwoPointSevenFiveUnitKeycapShadow
              key={`shadow-${keycapIndex}`}
              keycapIndex={keycapIndex}
              xPosForKeycap={xPosForKeycap}
              yPosForRow={yPosForRow}
              keycap={keycap}
              imageDataUrl={imageDataUrls.twoPointSevenFiveUnitKeycapShadow}
            />
          );
          components.push(shadowComponent);
        } else if (keycap.width === 2 && keycap.height === 1) {
          // a shadow for a 2 unit key will be created and added to the components array
          const shadowComponent = (
            <TwoUnitKeycapShadow
              key={`shadow-${keycapIndex}`}
              keycapIndex={keycapIndex}
              xPosForKeycap={xPosForKeycap}
              yPosForRow={yPosForRow}
              keycap={keycap}
              imageDataUrl={imageDataUrls.twoUnitKeycapShadow}
            />
          );
          components.push(shadowComponent);
        }
      }
    });

    return components; // returns the accumulated components (shadows)
  }, [triggerKeycapShadowRerender, imageDataUrls]); // this function creates the shadows for the keycaps and makes sure that the shadows are only re-computed (re-rendered) if the actual layout changes (and not everytime the user makes a change to the design)

  return staticKeycapShadows;
};

export default StaticKeycapShadows;
